import React, { useState, useEffect } from "react";
import PrintingLineItemCard from "./PrintingLineItemCard";
import Dropdown from "./Dropdown";
import { PlusIcon, SaveIcon } from "../icons";
import S from "../styles";

const makePrintItem = () => ({
  id: Date.now() + Math.random(),
  material: "", brand: "", color: "", weightGrams: "", partsNeeded: "",
});

export default function PrintingCalculatorPage({ filament, employees, editingJob, onSaveJob, onUpdateJob, onClearEdit, jobType }) {
  const [lineItems, setLineItems] = useState([makePrintItem()]);
  const [machineHours, setMachineHours] = useState("");
  const [machineRate, setMachineRate] = useState("");
  const [laborHours, setLaborHours] = useState("");
  const [laborRate, setLaborRate] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [profitMultiplier, setProfitMultiplier] = useState("");
  const [customerPriceInput, setCustomerPriceInput] = useState("");
  const [profitDriver, setProfitDriver] = useState(null);
  const [custName, setCustName] = useState("");
  const [custEmail, setCustEmail] = useState("");
  const [custPhone, setCustPhone] = useState("");
  const [saveMsg, setSaveMsg] = useState("");

  useEffect(() => {
    if (editingJob) {
      setLineItems(editingJob.lineItems || [makePrintItem()]);
      setMachineHours(editingJob.machineHours || "");
      setMachineRate(editingJob.machineRate || "");
      setLaborHours(editingJob.laborHours || "");
      setLaborRate(editingJob.laborRate || "");
      setSelectedEmployee(editingJob.selectedEmployee || "");
      setProfitMultiplier(editingJob.profitMultiplier || "");
      setCustomerPriceInput(editingJob.customerPriceInput || "");
      setProfitDriver(editingJob.profitDriver || null);
      setCustName(editingJob.customerName || "");
      setCustEmail(editingJob.customerEmail || "");
      setCustPhone(editingJob.customerPhone || "");
    }
  }, [editingJob]);

  const updateItem = (idx, u) => setLineItems((items) => items.map((it, i) => (i === idx ? u : it)));
  const removeItem = (idx) => setLineItems((items) => items.filter((_, i) => i !== idx));
  const addItem = () => setLineItems((items) => [...items, makePrintItem()]);
  const clearAll = () => setLineItems([makePrintItem()]);

  // Calculate line costs
  const lineSummaries = lineItems.map((item) => {
    const matched = filament.find((f) => f.material === item.material && f.brand === item.brand && f.color === item.color);
    if (!matched) return { cost: 0, complete: false };
    const spoolGrams = parseFloat(matched.weight) * (matched.weight.includes("kg") ? 1000 : 1);
    const pricePerGram = matched.price / (spoolGrams || 1000);
    const totalWeight = (parseFloat(item.weightGrams) || 0) * (parseInt(item.partsNeeded) || 0);
    const cost = totalWeight * pricePerGram;
    return { cost, complete: cost > 0 };
  });

  const completedItems = lineSummaries.filter((s) => s.complete);
  const materialTotal = lineSummaries.reduce((sum, s) => sum + s.cost, 0);
  const machineCost = (parseFloat(machineHours) || 0) * (parseFloat(machineRate) || 0);
  const laborTotal = (parseFloat(laborHours) || 0) * (parseFloat(laborRate) || 0);
  const grandTotal = materialTotal + machineCost + laborTotal;
  const rawMult = parseFloat(profitMultiplier) || 0;
  const rawCustP = parseFloat(customerPriceInput) || 0;

  let customerPrice = 0;
  let multiplier = 0;
  if (profitDriver === "multiplier" && rawMult > 0 && grandTotal > 0) {
    multiplier = rawMult;
    customerPrice = grandTotal * rawMult;
  } else if (profitDriver === "price" && rawCustP > 0 && grandTotal > 0) {
    customerPrice = rawCustP;
    multiplier = rawCustP / grandTotal;
  }
  const totalProfit = customerPrice > 0 ? customerPrice - grandTotal : 0;
  const hasAnyCost = completedItems.length > 0 || machineCost > 0 || laborTotal > 0;

  const handleMultiplierChange = (v) => { setProfitMultiplier(v); setProfitDriver(v ? "multiplier" : null); if (v) setCustomerPriceInput(""); };
  const handleCustomerPriceChange = (v) => { setCustomerPriceInput(v); setProfitDriver(v ? "price" : null); if (v) setProfitMultiplier(""); };

  const buildJobData = () => ({
    lineItems, machineHours, machineRate, laborHours, laborRate, selectedEmployee, profitMultiplier, customerPriceInput, profitDriver, jobType,
    customerName: custName, customerEmail: custEmail, customerPhone: custPhone,
    customerCost: Math.round(customerPrice), ourCost: Math.round(grandTotal), profit: Math.round(totalProfit),
  });

  const resetForm = () => {
    setLineItems([makePrintItem()]); setMachineHours(""); setMachineRate("");
    setLaborHours(""); setLaborRate(""); setSelectedEmployee("");
    setProfitMultiplier(""); setCustomerPriceInput(""); setProfitDriver(null);
    setCustName(""); setCustEmail(""); setCustPhone("");
  };

  const handleSave = () => {
    if (!custName) { setSaveMsg("Please enter a customer name."); setTimeout(() => setSaveMsg(""), 3000); return; }
    if (grandTotal <= 0) { setSaveMsg("Add materials or machine hours first."); setTimeout(() => setSaveMsg(""), 3000); return; }
    onSaveJob(buildJobData());
    setSaveMsg("Job saved to In Progress!");
    setTimeout(() => setSaveMsg(""), 3000);
    resetForm();
  };

  const handleUpdate = () => {
    if (!custName) { setSaveMsg("Please enter a customer name."); setTimeout(() => setSaveMsg(""), 3000); return; }
    onUpdateJob({ ...editingJob, ...buildJobData() });
    setSaveMsg("Changes saved!");
    setTimeout(() => setSaveMsg(""), 3000);
  };

  const handleClearEdit = () => { onClearEdit(); resetForm(); };

  return (
    <div className="calc-layout" style={{ display: "flex", gap: 28, alignItems: "flex-start" }}>
      <div style={{ flex: "1 1 0%", minWidth: 0 }}>
        {editingJob && (
          <div style={{ ...S.editBanner, marginBottom: 16 }}>
            <span style={{ fontSize: 15 }}>Editing <strong>{editingJob.jobNumber}</strong> — {editingJob.customerName}</span>
            <button onClick={handleClearEdit} style={S.clearBtn}>New Job</button>
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {lineItems.map((item, idx) => (
            <PrintingLineItemCard
              key={item.id}
              item={item}
              index={idx}
              filament={filament}
              onChange={(u) => updateItem(idx, u)}
              onRemove={() => removeItem(idx)}
              canRemove={lineItems.length > 1}
            />
          ))}
        </div>
        <div style={{ marginTop: 20, display: "flex", gap: 10, alignItems: "center" }}>
          <button onClick={addItem} style={S.addLineBtn}><PlusIcon /> Add Another Filament</button>
          {lineItems.length > 1 && <button onClick={clearAll} style={S.clearBtn}>Clear All</button>}
        </div>

        {/* Machine Hours */}
        <div style={S.laborCard}>
          <div style={S.laborCardHeader}>
            <span style={S.lineCardNumber}>Machine Hours</span>
            {machineCost > 0 && <span style={S.lineCardCost}>${machineCost.toFixed(2)}</span>}
          </div>
          <div style={S.laborCardBody}>
            <div style={S.paramsRow}>
              <div style={{ flex: 1 }}>
                <label style={S.label}>Machine Hours</label>
                <input type="number" min="0" step="0.5" value={machineHours} onChange={(e) => setMachineHours(e.target.value)} placeholder="e.g. 12" style={S.input} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={S.label}>Cost Per Hour ($)</label>
                <input type="number" min="0" step="0.5" value={machineRate} onChange={(e) => setMachineRate(e.target.value)} placeholder="e.g. 5" style={S.input} />
              </div>
            </div>
            {machineCost > 0 && (
              <div style={S.laborResultBar}>
                <div style={S.lineResultItem}>
                  <span style={{ color: "rgba(10,10,15,0.58)", fontSize: 13, textTransform: "uppercase", letterSpacing: 1 }}>Hours</span>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 18, color: "#0A0A0F" }}>{parseFloat(machineHours) || 0}</span>
                </div>
                <div style={{ width: 1, height: 28, background: "#F4F2EC" }} />
                <div style={S.lineResultItem}>
                  <span style={{ color: "rgba(10,10,15,0.58)", fontSize: 13, textTransform: "uppercase", letterSpacing: 1 }}>Rate</span>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 18, color: "#0A0A0F" }}>${parseFloat(machineRate).toFixed(2)}</span>
                </div>
                <div style={{ width: 1, height: 28, background: "#F4F2EC" }} />
                <div style={S.lineResultItem}>
                  <span style={{ color: "rgba(10,10,15,0.58)", fontSize: 13, textTransform: "uppercase", letterSpacing: 1 }}>Machine Total</span>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 18, color: "#0033FF" }}>${machineCost.toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Labor */}
        <div style={S.laborCard}>
          <div style={S.laborCardHeader}>
            <span style={S.lineCardNumber}>Labor Cost</span>
            {laborTotal > 0 && <span style={S.lineCardCost}>${laborTotal.toFixed(2)}</span>}
          </div>
          <div style={S.laborCardBody}>
            <Dropdown
              label="Select Employee"
              value={selectedEmployee}
              onChange={(v) => {
                setSelectedEmployee(v);
                const emp = employees.find((e) => String(e.id) === String(v));
                if (emp) setLaborRate(String(emp.rate));
              }}
              options={employees.map((e) => ({ value: String(e.id), label: `${e.name} — ${e.position} (${e.rate.toFixed(2)}/hr)` }))}
              placeholder="Choose employee..."
            />
            <div style={S.paramsRow}>
              <div style={{ flex: 1 }}>
                <label style={S.label}>Hours on Job</label>
                <input type="number" min="0" step="0.5" value={laborHours} onChange={(e) => setLaborHours(e.target.value)} placeholder="e.g. 2" style={S.input} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={S.label}>Cost Per Hour ($)</label>
                <input type="number" min="0" step="0.5" value={laborRate} onChange={(e) => { setLaborRate(e.target.value); setSelectedEmployee(""); }} placeholder="e.g. 17" style={S.input} />
              </div>
            </div>
            {laborTotal > 0 && (
              <div style={S.laborResultBar}>
                <div style={S.lineResultItem}>
                  <span style={{ color: "rgba(10,10,15,0.58)", fontSize: 13, textTransform: "uppercase", letterSpacing: 1 }}>Hours</span>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 18, color: "#0A0A0F" }}>{parseFloat(laborHours) || 0}</span>
                </div>
                <div style={{ width: 1, height: 28, background: "#F4F2EC" }} />
                <div style={S.lineResultItem}>
                  <span style={{ color: "rgba(10,10,15,0.58)", fontSize: 13, textTransform: "uppercase", letterSpacing: 1 }}>Rate</span>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 18, color: "#0A0A0F" }}>${parseFloat(laborRate).toFixed(2)}</span>
                </div>
                <div style={{ width: 1, height: 28, background: "#F4F2EC" }} />
                <div style={S.lineResultItem}>
                  <span style={{ color: "rgba(10,10,15,0.58)", fontSize: 13, textTransform: "uppercase", letterSpacing: 1 }}>Labor Total</span>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 18, color: "#0033FF" }}>${laborTotal.toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right sidebar */}
      <div style={{ width: 320, flexShrink: 0, position: "sticky", top: 80 }}>
        {/* Job Summary */}
        <div style={S.grandTotalCard}>
          <div style={S.grandTotalHeader}>
            <span style={{ fontSize: 13, fontWeight: 600, textTransform: "uppercase", letterSpacing: 2, color: "rgba(10,10,15,0.58)" }}>Job Summary</span>
            {completedItems.length > 0 && <span style={{ fontSize: 14, color: "rgba(10,10,15,0.58)" }}>{completedItems.length} item{completedItems.length !== 1 ? "s" : ""}</span>}
          </div>
          {!hasAnyCost ? (
            <div style={{ padding: "40px 20px", textAlign: "center", color: "rgba(10,10,15,0.58)", fontSize: 15, lineHeight: 1.7 }}>
              Configure filament and job<br />parameters to see your estimate.
            </div>
          ) : (
            <>
              {lineItems.map((item, idx) => {
                const s = lineSummaries[idx];
                if (!s.complete) return null;
                return (
                  <div key={item.id} style={S.summaryRow}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
                      <span style={S.summaryNumber}>{idx + 1}</span>
                      <div style={{ overflow: "hidden", minWidth: 0 }}>
                        <div style={{ fontSize: 14, color: "#0A0A0F", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {item.material} — {item.color}
                        </div>
                        <div style={{ fontSize: 13, color: "rgba(10,10,15,0.58)", marginTop: 1 }}>
                          {item.brand} · {(parseFloat(item.weightGrams) || 0) * (parseInt(item.partsNeeded) || 0)}g total
                        </div>
                      </div>
                    </div>
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, fontSize: 15, color: "#0A0A0F", flexShrink: 0 }}>
                      ${s.cost.toFixed(2)}
                    </span>
                  </div>
                );
              })}
              {completedItems.length > 0 && (machineCost > 0 || laborTotal > 0) && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 20px", borderBottom: "1px solid rgba(10,10,15,0.12)" }}>
                  <span style={{ fontSize: 13, color: "rgba(10,10,15,0.58)", textTransform: "uppercase", letterSpacing: 1 }}>Filament Subtotal</span>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, fontSize: 15, color: "rgba(10,10,15,0.58)" }}>${materialTotal.toFixed(2)}</span>
                </div>
              )}
              {machineCost > 0 && (
                <div style={S.summaryRow}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
                    <span style={{ ...S.summaryNumber, background: "#1a2a3d", color: "#8b5cf6" }}>M</span>
                    <div><div style={{ fontSize: 14, color: "#0A0A0F" }}>Machine</div><div style={{ fontSize: 13, color: "rgba(10,10,15,0.58)", marginTop: 1 }}>{parseFloat(machineHours) || 0} hrs × ${parseFloat(machineRate).toFixed(2)}/hr</div></div>
                  </div>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, fontSize: 15, color: "#0A0A0F", flexShrink: 0 }}>${machineCost.toFixed(2)}</span>
                </div>
              )}
              {laborTotal > 0 && (
                <div style={S.summaryRow}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
                    <span style={{ ...S.summaryNumber, background: "#3d2a1a", color: "#0033FF" }}>L</span>
                    <div><div style={{ fontSize: 14, color: "#0A0A0F" }}>Labor</div><div style={{ fontSize: 13, color: "rgba(10,10,15,0.58)", marginTop: 1 }}>{parseFloat(laborHours) || 0} hrs × ${parseFloat(laborRate).toFixed(2)}/hr</div></div>
                  </div>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, fontSize: 15, color: "#0A0A0F", flexShrink: 0 }}>${laborTotal.toFixed(2)}</span>
                </div>
              )}
              <div style={S.grandTotalBar}>
                <div>
                  <span style={{ fontSize: 13, textTransform: "uppercase", letterSpacing: 2, color: "#FFFFFF", opacity: 0.6 }}>Total Job Cost</span>
                </div>
                <span style={{ fontSize: 30, fontWeight: 800, fontFamily: "'JetBrains Mono', monospace", color: "#FFFFFF" }}>${grandTotal.toFixed(2)}</span>
              </div>
            </>
          )}
        </div>

        {/* Profit */}
        <div style={{ ...S.grandTotalCard, marginTop: 16 }}>
          <div style={S.grandTotalHeader}>
            <span style={{ fontSize: 13, fontWeight: 600, textTransform: "uppercase", letterSpacing: 2, color: "rgba(10,10,15,0.58)" }}>Profit</span>
          </div>
          <div style={{ padding: "16px 20px" }}>
            <div style={{ display: "flex", gap: 12, alignItems: "flex-end" }}>
              <div style={{ flex: 1 }}>
                <label style={S.label}>Multiplier</label>
                <input type="number" min="0" step="0.1" value={profitDriver === "price" && multiplier > 0 ? multiplier.toFixed(2) : profitMultiplier} onChange={(e) => handleMultiplierChange(e.target.value)} placeholder="e.g. 5" style={{ ...S.input, ...(profitDriver === "multiplier" ? S.activeInput : {}) }} readOnly={profitDriver === "price"} onClick={() => { if (profitDriver === "price") handleMultiplierChange(""); }} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={S.label}>Customer Price ($)</label>
                <input type="number" min="0" step="1" value={profitDriver === "multiplier" && customerPrice > 0 ? customerPrice.toFixed(2) : customerPriceInput} onChange={(e) => handleCustomerPriceChange(e.target.value)} placeholder="e.g. 500" style={{ ...S.input, ...(profitDriver === "price" ? S.activeInput : {}) }} readOnly={profitDriver === "multiplier"} onClick={() => { if (profitDriver === "multiplier") handleCustomerPriceChange(""); }} />
              </div>
            </div>
            {profitDriver && multiplier > 0 && grandTotal > 0 && (
              <div style={{ marginTop: 10, fontSize: 13, color: "rgba(10,10,15,0.58)", textAlign: "center" }}>${grandTotal.toFixed(2)} × {multiplier.toFixed(2)} = ${customerPrice.toFixed(2)}</div>
            )}
            {customerPrice > 0 && grandTotal > 0 && (
              <div style={S.profitBar}>
                <div>
                  <span style={{ fontSize: 13, textTransform: "uppercase", letterSpacing: 2, color: "#0c3a2a", opacity: 0.7 }}>Total Profit</span>
                  <div style={{ fontSize: 13, color: "#0c3a2a", opacity: 0.5, marginTop: 2 }}>{multiplier > 1 ? ((multiplier - 1) / multiplier * 100).toFixed(0) + "% margin" : ""}</div>
                </div>
                <span style={{ fontSize: 26, fontWeight: 800, fontFamily: "'JetBrains Mono', monospace", color: "#0c3a2a" }}>${totalProfit.toFixed(2)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Save Job */}
        <div style={{ ...S.grandTotalCard, marginTop: 16 }}>
          <div style={S.grandTotalHeader}>
            <span style={{ fontSize: 13, fontWeight: 600, textTransform: "uppercase", letterSpacing: 2, color: "rgba(10,10,15,0.58)" }}>{editingJob ? "Update Job" : "Save Job"}</span>
          </div>
          <div style={{ padding: "16px 20px" }}>
            <div style={{ marginBottom: 12 }}>
              <label style={S.label}>Customer Name *</label>
              <input type="text" value={custName} onChange={(e) => setCustName(e.target.value)} placeholder="e.g. Brooklyn Signworks" style={S.input} />
            </div>
            <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={S.label}>Email</label>
                <input type="email" value={custEmail} onChange={(e) => setCustEmail(e.target.value)} placeholder="info@company.com" style={S.input} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={S.label}>Phone</label>
                <input type="tel" value={custPhone} onChange={(e) => setCustPhone(e.target.value)} placeholder="(212) 555-1234" style={S.input} />
              </div>
            </div>
            {saveMsg && <div style={{ fontSize: 14, color: saveMsg.includes("saved") || saveMsg.includes("Changes") ? "#34d399" : "#f87171", marginBottom: 10, textAlign: "center" }}>{saveMsg}</div>}
            {editingJob ? (
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={handleUpdate} style={{ ...S.saveJobBtn, flex: 1 }}><SaveIcon /> Save Changes</button>
                <button onClick={handleClearEdit} style={{ ...S.clearBtn, padding: "10px 16px" }}>Cancel</button>
              </div>
            ) : (
              <button onClick={handleSave} style={S.saveJobBtn}><SaveIcon /> Save to In Progress</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
